import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionItem,
  Button as CarbonButton,
  InlineNotification,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tooltip,
} from '@carbon/react';
import { Add, Close, Information, Laptop, Screen } from '@carbon/react/icons';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { EntitySearchDialog } from 'pages/entities/entity-search-dialog';
import type { ApiPayloads } from 'types/api-payloads';
import type { EntityType as EntityListType } from 'lib/entities';
import type { EntitySearchResult } from 'pages/entities/list/types';
import type {
  ReconcileCreateResponse,
  ReconcilePreviewResponse,
} from 'types/api-responses';
import ReconcileCandidateTable from 'pages/documents/structuring/reconcile/reconcile-candidate-table';
import { useAliasClusterStates } from 'pages/documents/structuring/reconcile/use-alias-pill-states';
import { usePaneResize } from 'pages/documents/structuring/reconcile/use-pane-resize';
import { useReconcilePreview } from 'pages/documents/structuring/reconcile/use-reconcile-preview';
import { ALIAS_CLUSTER_GRAPH_SELECTOR } from 'pages/documents/structuring/reconcile/alias-cluster-graph';
import RowAliasGraphLink from 'pages/documents/structuring/reconcile/row-alias-graph-link';
import { type ReconcileCandidate } from 'pages/documents/structuring/reconcile/types';
import { actionErrorMessage } from 'utils/api-errors';
import {
  canonizedCandidate,
  userAddedCandidate,
} from './reconcile-candidate-helpers';
import { type EntityCanonizeResponse } from './atoms';
import EntityConsolidationPanel from './entity-consolidation-panel';
import EntityConsolidationSurvivors from './entity-consolidation-survivors';
import { useHighlight } from './use-highlight';
import { useStaleSelection } from './hooks';
import DocImagePanel, {
  type DocImageLayout,
} from 'pages/documents/structuring/reconcile/doc-image-panel';
import EntityCanonizePanel from './entity-canonize-panel';
import {
  useSurvivorResetConfirmation,
  SurvivorResetModal,
} from './survivor-reset-confirmation';
import {
  addressFlaggedCheckedIds,
  buildGroupsPayload,
  buildOperationTags,
  checkedEntityIds,
  consolidationReducer,
  establishedCanonicalType,
  initConsolidation,
  isNoOp,
  lockedEntityIds,
  lockNotice,
  orderedCandidates,
  submitBlockers,
  type ConsolidationAction,
  type ConsolidationCandidate,
  type ConsolidationState,
  type GroupPayload,
} from './consolidation-utils';
import styles from 'pages/documents/structuring/reconcile/reconcile-tearsheet.module.scss';
import {
  FlagOverrideModal,
  isFlagOverrideRequired,
  type FlaggedEntitySummary,
} from 'components/flag-override-modal';
import { format0 } from 'utils/number';
import { entityRefLabel } from 'utils/entity-id';

// A record the server named as carrying an unresolved address-confidence flag.
export interface ReviewFlaggedEntity {
  id: number;
  name: string | null;
}

// A reconcile submit rejected because included records still carry an unresolved
// address-confidence flag: the 422 names those records so the UI can mark them.
// Mirrors isFlagOverrideRequired for the (override-able) consolidation-risk gate;
// this block is hard — the records must be resolved or removed, not overridden.
export const isReviewFlagRejection = (
  err: unknown
): err is { reviewFlaggedEntities: ReviewFlaggedEntity[] } => {
  const list = (err as { reviewFlaggedEntities?: unknown } | null | undefined)
    ?.reviewFlaggedEntities;
  return Array.isArray(list) && list.length > 0;
};

// The consolidation reducer (R21) plus a host-only reinit meta-action, so the
// tearsheet can reset the whole model on open with one dispatch instead of a
// setState side-channel. Model refusals are folded into state.lastRefusal.
type HostConsolidationAction =
  ConsolidationAction | { type: '__reinit'; state: ConsolidationState };

function hostReducer(
  state: ConsolidationState,
  action: HostConsolidationAction
): ConsolidationState {
  return action.type === '__reinit'
    ? action.state
    : consolidationReducer(state, action);
}

// The create request body — identical across surfaces; only the endpoint (and,
// for preview, whether `groups` is included) differs, which the wrappers own.
export interface ReconcileCreateData {
  entity_ids: number[];
  groups: GroupPayload[];
  version_tokens: Record<string, string>;
  session_id: string;
  override_rationale?: string;
  false_positive_entity_ids?: number[];
}

type ReconcileCreateError = ApiPayloads['reconcileCreate']['error'];

// Surface-specific configuration for the one shared tearsheet. Everything the
// entity page and Match Review differ on is an INPUT here (anchor rows, pre-check,
// endpoints, title); the layout and all behavior below are identical for both.
export interface ConsolidationTearsheetProps<
  TCreate = ReconcileCreateResponse,
> {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  title: string;
  // The display rows (the anchor pinned first), and how each maps to the model.
  originalCandidates: ReconcileCandidate[];
  toModel: (c: ReconcileCandidate) => ConsolidationCandidate;
  // §3.2 pre-check seeded into initConsolidation (surface-specific strategy).
  preCheckIds: number[];
  // "More candidates found" notice inputs.
  moreFound: boolean;
  totalBeforeCap: number;
  shownCount: number;
  // Add Entities modal scoping.
  primaryEntityId?: number;
  primaryEntityType: EntityListType;
  // Mutation wiring — the wrapper owns the atom + URL (and, for preview, the exact
  // payload); the shared component supplies the ids + computed groups + handlers.
  previewLoading: boolean;
  createLoading: boolean;
  submitPreview: (
    ids: number[],
    groups: GroupPayload[],
    onSuccess: (data: ReconcilePreviewResponse) => void,
    onError: (message: string) => void
  ) => void;
  submitCreate: (
    data: ReconcileCreateData,
    onSuccess: (data: TCreate) => void,
    onError: (err?: ReconcileCreateError) => void
  ) => void;
  onComplete: (result: TCreate, namesById: Record<number, string>) => void;
  // Fired when an in-place "Add as New" canonization commits (§3.14). Each is its
  // own transaction, so the caller counts them to disclose in the Undo prompt.
  onCanonized?: () => void;
  // Appended to the "What Reconcile will do" summary (e.g. the other parties a
  // shared Match Review anchor also updates).
  whatReconcileWillDoNote?: string | null;
  // Show the Operator tag on operator rows. Only the entity Reconcile Entities
  // modal (entity page or Entities list) sets it; Match Review leaves it off.
  showOperatorTag?: boolean;
}

// Phase 2 (default-to-dedupe) reconcile tearsheet, shared by the entity-page and
// Match Review surfaces (they pass surface config, above). The left panel is the
// shared candidate table; the right panel is the Entity Consolidation model +
// Reconciliation Result preview. Selection is owned by the consolidation state
// (the two panels are two paths to it), and submit sends the user-defined `groups`.
function ConsolidationTearsheet<TCreate = ReconcileCreateResponse>({
  open,
  onClose,
  sessionId,
  title,
  originalCandidates,
  toModel,
  preCheckIds,
  moreFound,
  totalBeforeCap,
  shownCount,
  primaryEntityId,
  primaryEntityType,
  previewLoading,
  createLoading,
  submitPreview,
  submitCreate,
  onComplete,
  onCanonized,
  whatReconcileWillDoNote,
  showOperatorTag = false,
}: ConsolidationTearsheetProps<TCreate>) {
  const originalModelCandidates = useMemo<ConsolidationCandidate[]>(
    () => originalCandidates.map(toModel),
    [originalCandidates, toModel]
  );

  const buildInitial = useCallback(
    (): ConsolidationState =>
      initConsolidation(originalModelCandidates, { preCheckIds }),
    [originalModelCandidates, preCheckIds]
  );

  const [state, dispatch] = useReducer(hostReducer, undefined, buildInitial);
  // Latest state for runPreview without churning its identity (which would
  // re-fire the preview effect on every edit); preview sends the same `groups`
  // submit does, so the server preview honors the user's grouping (not
  // name-equivalence) and its fingerprints cover the actual selection.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Rich display data keyed by entity id. The MODEL (state.candidates +
  // orderedCandidates) is the single source of truth for which candidates are
  // present and their order (R21); this map only supplies the table's display
  // fields (address, source label, flags), never membership/selection.
  const [presentation, setPresentation] = useState<
    Record<number, ReconcileCandidate>
  >(() => Object.fromEntries(originalCandidates.map((c) => [c.id, c])));

  const reconcileCandidates = useMemo<ReconcileCandidate[]>(
    () =>
      orderedCandidates(state)
        .map((mc) => presentation[mc.id])
        .filter((c): c is ReconcileCandidate => Boolean(c)),
    [state, presentation]
  );

  // Display data by id for the survivors preview (names, types, addresses).
  const candidatesById = useMemo(
    () => new Map(reconcileCandidates.map((c) => [c.id, c])),
    [reconcileCandidates]
  );

  // An address-confidence flag was cleared from a row's Documents drilldown (#26):
  // drop the flag on both the model (name flag + submit gate) and the presentation
  // (the left-table row marker) so the UI updates without a full candidate refetch.
  const handleFlagCleared = useCallback((entityId: number) => {
    dispatch({ type: 'clearAddressFlag', id: entityId });
    setPresentation((prev) => {
      const c = prev[entityId];
      if (!c?.addressFlagged) return prev;
      return { ...prev, [entityId]: { ...c, addressFlagged: false } };
    });
  }, []);

  // The submit was rejected because one or more included records still carry an
  // unresolved address review flag the client hadn't caught (a folded family
  // member, an Add-Entities addition, or a flag added after load).
  // Set the flag on both the model (name marker + submit gate) and the
  // presentation (left-table row marker) so the offending rows light up instead
  // of leaving the user to guess which record the generic error meant.
  const markReviewFlagged = useCallback((entityIds: number[]) => {
    if (entityIds.length === 0) return;
    dispatch({ type: 'flagAddresses', ids: entityIds });
    setPresentation((prev) => {
      const next = { ...prev };
      entityIds.forEach((id) => {
        const c = next[id];
        if (c && !c.addressFlagged) next[id] = { ...c, addressFlagged: true };
      });
      return next;
    });
  }, []);

  // Inline Document viewer (mock v25 / §3.13): the DocImagePanel driven by
  // whichever document row's View button was clicked. Toggling the open document
  // closes the pane.
  const [viewedDocument, setViewedDocument] = useState<{
    id: number;
    name?: string;
  } | null>(null);
  const [docImageLayout, setDocImageLayout] = useState<DocImageLayout>('side');
  const handleViewDocument = useCallback(
    (documentId: number, documentName?: string) => {
      setViewedDocument((prev) => {
        return prev?.id === documentId
          ? null
          : { id: documentId, name: documentName };
      });
    },
    []
  );

  const [error, setError] = useState<string | null>(null);
  // The single left-pane highlight (§3.4), driven by whichever surface last owned
  // it — the Entity Consolidation name selection or a survivor's View Documents
  // row — modelled as one discriminated-union state so the two can never be lit at
  // once. The panel's name selection is controlled from here via selectedNameKeys.
  const {
    highlightedIds,
    highlightedDocEntityId,
    selectedNameKeys,
    selectNames,
    highlightDocument,
    reset: resetHighlight,
  } = useHighlight(state);
  // R12: the distinct name the right-panel funnel is filtering the left list to
  // (null = show all). Cleared automatically when that name leaves the model.
  const [filterNameKey, setFilterNameKey] = useState<string | null>(null);
  useEffect(() => {
    if (filterNameKey && !state.names.some((n) => n.key === filterNameKey)) {
      setFilterNameKey(null);
    }
  }, [filterNameKey, state.names]);
  const filteredName = useMemo(() => {
    return filterNameKey
      ? (state.names.find((n) => n.key === filterNameKey) ?? null)
      : null;
  }, [filterNameKey, state.names]);
  // The candidate rows actually shown in the left table: all of them, or — when
  // the funnel is active — only the filtered name's members (R12).
  const displayedCandidates = useMemo(() => {
    const base = filteredName
      ? reconcileCandidates.filter((c) =>
          new Set(filteredName.memberIds).has(c.id)
        )
      : reconcileCandidates;
    // Pin the anchor (staged/viewed) first, then User-Added entities (§3.9), then
    // the rest in their existing order — so the tile (card) view mirrors the
    // table's below-the-anchor pinning of a just-added entity.
    const isUserAdded = (c: ReconcileCandidate) =>
      c.sourceLabel?.startsWith('User Added') ?? false;
    const staged = base.filter((c) => c.isStaged);
    const userAdded = base.filter((c) => !c.isStaged && isUserAdded(c));
    const rest = base.filter((c) => !c.isStaged && !isUserAdded(c));
    return [...staged, ...userAdded, ...rest];
  }, [filteredName, reconcileCandidates]);
  // Left-table toolbar (mock parity): a search box + a layout toggle, alongside
  // the funnel-driven name filter above.
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateCompact, setCandidateCompact] = useState(false);
  const searchedCandidates = useMemo(() => {
    const q = candidateSearch.trim().toLowerCase();
    if (!q) return displayedCandidates;
    return displayedCandidates.filter(
      (c) => c.name.toLowerCase().includes(q) || String(c.id).includes(q)
    );
  }, [displayedCandidates, candidateSearch]);
  const [flaggedForOverride, setFlaggedForOverride] = useState<
    FlaggedEntitySummary[] | null
  >(null);
  const [addEntitiesOpen, setAddEntitiesOpen] = useState(false);
  const [canonizeTargetId, setCanonizeTargetId] = useState<number | null>(null);

  // Re-init fresh each time the tearsheet opens (one dispatch resets the model;
  // the refusal lives in state, so it resets with it).
  useEffect(() => {
    if (open) {
      dispatch({ type: '__reinit', state: buildInitial() });
      setError(null);
      resetHighlight();
      setFlaggedForOverride(null);
      setPresentation(
        Object.fromEntries(originalCandidates.map((c) => [c.id, c]))
      );
      setAddEntitiesOpen(false);
      setCanonizeTargetId(null);
      setViewedDocument(null);
      setDocImageLayout('side');
    }
  }, [open, buildInitial, originalCandidates, resetHighlight]);

  const { previewWidth, columnsRef, startResize } = usePaneResize();
  // Second divider — resizes the document-image pane within the work area
  // (Erik feedback: let the doc image resize, not just the left/right panels).
  // `minOther` reserves room for the candidates|preview split inside .columns
  // (candidates + preview minimums) so dragging the doc pane wide can't starve
  // that split and make its content overflow.
  const {
    previewWidth: docImageWidth,
    columnsRef: workAreaRef,
    startResize: startDocResize,
  } = usePaneResize({ minOther: 680 });
  // Third divider — resizes the stacked document-image strip vertically (the
  // .body is a flex column, so the strip's height is measured off its bottom).
  const {
    previewWidth: docImageHeight,
    columnsRef: bodyRef,
    startResize: startDocVResize,
  } = usePaneResize({ axis: 'vertical', minSize: 160, minOther: 300 });

  const selectedIds = useMemo(() => new Set(checkedEntityIds(state)), [state]);

  const runPreview = useCallback(
    (
      ids: number[],
      onSuccess: (data: ReconcilePreviewResponse) => void,
      onError: (message: string) => void
    ) =>
      submitPreview(
        ids,
        buildGroupsPayload(stateRef.current),
        onSuccess,
        onError
      ),
    [submitPreview]
  );

  // Preview is used only for the load-time fingerprints (optimistic-concurrency
  // tokens); the grouping is client-side in Phase 2.
  const { preview, refreshPreview } =
    useReconcilePreview<ReconcilePreviewResponse>(
      open,
      selectedIds,
      runPreview,
      setError
    );

  const aliasStates = useAliasClusterStates(
    open ? reconcileCandidates.map((c) => c.id) : []
  );

  // Left-table Source-cell operation tags: "Deduplicate N" on every member of
  // survivor group N, "Surviving Entity N" on that group's representative (mock
  // v25 — numbered, with the survivor rendered high-contrast).
  const operationTags = useMemo(() => buildOperationTags(state), [state]);

  // §3.3 survivor-reset confirmation (single deselect, header select-all clear,
  // right-panel trashcan) — shared with both surfaces via one hook.
  const reset = useSurvivorResetConfirmation(dispatch, stateRef);
  const onToggle = reset.toggle;
  const handleConfirmRemoveName = reset.requestRemoveName;

  const namesById = useMemo(
    () =>
      Object.fromEntries(
        reconcileCandidates.map((c) => [c.id, c.name])
      ) as Record<number, string>,
    [reconcileCandidates]
  );

  const handleStaleSelection = useStaleSelection({
    open,
    primaryEntityId,
    selectedIds,
    loadedFingerprints: preview?.fingerprints,
    namesById,
    presentation,
    dispatch,
    refreshPreview,
    setError,
    setPresentation,
  });

  // Pure model-level submit gating (R21): no-selection, no-op, outstanding
  // rationale, address-flagged. Host-level gates (preview loaded, in-flight
  // request) layer on top. Memoized so unrelated re-renders don't recompute it.
  const blockers = useMemo(() => submitBlockers(state), [state]);
  const noOp = useMemo(() => isNoOp(state), [state]);

  // Address-confidence flags block submission only (#26); the count drives the
  // banner (the block itself is a submitBlockers entry).
  const flaggedCount = useMemo(
    () => addressFlaggedCheckedIds(state).length,
    [state]
  );

  const canSubmit =
    Boolean(preview) &&
    !previewLoading &&
    !createLoading &&
    blockers.length === 0;

  // Entities locked because a canonical survivor of the opposite type exists (#27),
  // and the banner explaining why (mock v19).
  const lockedIds = useMemo(() => new Set(lockedEntityIds(state)), [state]);
  const lockMessage = useMemo(() => lockNotice(state), [state]);

  // Add Entities type lock (Decision #32): once a canonical survivor is
  // established, only that type + Imported can be added — so the modal's type
  // filter is fixed to that set and the incompatible-type noise is removed.
  const addEntitiesAllowedTypes = useMemo<EntityListType[] | undefined>(() => {
    const established = establishedCanonicalType(state);
    if (established === 'IndividualEntity') return ['Individual', 'Imported'];
    if (established === 'OrganizationEntity')
      return ['Organization', 'Imported'];
    return undefined;
  }, [state]);

  // Header "select all" (mock v25): the left table's column-header checkbox
  // checks every currently-shown selectable candidate — skipping rows locked by
  // the opposite-type guard (#27) or a hard address block — or clears them all
  // when everything's already in.
  const selectableIds = useMemo(
    () =>
      searchedCandidates
        .filter((c) => !lockedIds.has(c.id) && !c.flagged)
        .map((c) => c.id),
    [searchedCandidates, lockedIds]
  );
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectableIds.some((id) => selectedIds.has(id));
  const onToggleAll = useCallback(
    () => reset.toggleAll(selectableIds, selectedIds, allSelected),
    [reset, selectableIds, selectedIds, allSelected]
  );

  // The always-visible "What Reconcile will do" summary (#29): a plain-language
  // preview, or a "nothing will happen yet" prompt when the selection is short.
  const whatReconcileWillDo = useMemo(() => {
    const groups = buildGroupsPayload(state);
    if (noOp || groups.length === 0) {
      return 'Nothing will happen yet — select entities to deduplicate into a survivor, or designate a second survivor to keep distinct (aliased).';
    }
    const merged = groups.reduce((n, g) => n + g.member_ids.length, 0);
    const parts = [
      `${groups.length} surviving ${
        groups.length === 1 ? 'entity' : 'entities'
      }`,
    ];
    if (merged > 0)
      parts.push(`${merged} record${merged === 1 ? '' : 's'} merged in`);
    if (groups.length > 1)
      parts.push(`${groups.length} kept distinct and aliased`);
    return `On Reconcile: ${parts.join(', ')}.`;
  }, [state, noOp]);

  // Notifications are split per panel (mock parity): candidate-list notices go in
  // the left "Current Suggestions" accordion; consolidation/submission notices go
  // in the right "Entity Consolidation" accordion. Each accordion shows its own
  // count and expands/collapses independently.
  const leftNotifications: React.ReactNode[] = [
    // Always-visible "What Reconcile will do" summary (#29) — lives in the left
    // (Current Suggestions) notifications accordion.
    <InlineNotification
      key="what"
      kind={noOp ? 'info' : 'success'}
      title="What Reconcile will do —"
      subtitle={
        whatReconcileWillDoNote
          ? `${whatReconcileWillDo} ${whatReconcileWillDoNote}`
          : whatReconcileWillDo
      }
      lowContrast
      hideCloseButton
      className={styles.error}
    />,
    filteredName && (
      <InlineNotification
        key="filtered"
        kind="info"
        lowContrast
        title="Filtered"
        subtitle={`Showing only "${filteredName.label}" — clear to see all candidates.`}
        onCloseButtonClick={() => setFilterNameKey(null)}
      />
    ),
    moreFound && (
      <InlineNotification
        key="more"
        kind="info"
        title="More candidates found —"
        subtitle={`Showing the top ${shownCount} of ${format0(
          totalBeforeCap
        )} candidates that met the threshold.`}
        lowContrast
        hideCloseButton
        className={styles.error}
      />
    ),
    flaggedCount > 0 && (
      <InlineNotification
        key="flags"
        kind="warning"
        title="Address-confidence flags —"
        subtitle={`${flaggedCount} included ${
          flaggedCount === 1 ? 'entity has' : 'entities have'
        } an unresolved address-confidence flag. Resolve the flag or remove the entity from the reconciliation before reconciling.`}
        lowContrast
        hideCloseButton
        className={styles.error}
      />
    ),
  ].filter(Boolean);

  const rightNotifications: React.ReactNode[] = [
    error && (
      <InlineNotification
        key="error"
        kind="error"
        title="Reconcile failed"
        subtitle={error}
        lowContrast
        hideCloseButton
        className={styles.error}
      />
    ),
    state.lastRefusal && (
      <InlineNotification
        key="refusal"
        kind="warning"
        title="Action not allowed —"
        subtitle={state.lastRefusal}
        lowContrast
        onCloseButtonClick={() => dispatch({ type: 'clearRefusal' })}
        className={styles.error}
      />
    ),
    lockMessage && (
      <InlineNotification
        key="lock"
        kind="info"
        title="Some entities are locked —"
        subtitle={lockMessage}
        lowContrast
        hideCloseButton
        className={styles.error}
      />
    ),
  ].filter(Boolean);

  // Entities chosen from the Add Entities library search (§3.9): append the
  // fresh ones as "User Added" candidates and action them in the model.
  const handleAddEntities = (results: EntitySearchResult[]) => {
    setAddEntitiesOpen(false);
    // The model's candidate set is the source of truth for what's already here.
    const fresh = results
      .filter((r) => !state.candidates[r.id])
      .map(userAddedCandidate);
    if (fresh.length === 0) return;
    setPresentation((prev) => {
      const next = { ...prev };
      fresh.forEach((c) => {
        next[c.id] = c;
      });
      return next;
    });
    dispatch({ type: 'addCandidates', candidates: fresh.map(toModel) });
  };

  // Imported entities can be converted in place ("Add as New", §3.9); the row
  // action opens the canonize panel. (Address-flagged entities are excluded.)
  const addAsNewIds = useMemo(
    () =>
      new Set(
        reconcileCandidates
          .filter((c) => c.type === 'ImportedEntity' && !c.flagged)
          .map((c) => c.id)
      ),
    [reconcileCandidates]
  );
  const canonizeTarget = useMemo(
    () => reconcileCandidates.find((c) => c.id === canonizeTargetId) ?? null,
    [reconcileCandidates, canonizeTargetId]
  );

  // A completed Add as New: the imported entity is retired server-side into a
  // brand-new canonical; drop it from the list and add the canonical as its own
  // survivor (§3.9 "After completion"), tagged "User Added - Add as New".
  const handleCanonized = (response: EntityCanonizeResponse) => {
    const importedId = canonizeTargetId;
    setCanonizeTargetId(null);
    if (importedId === null) return;
    const canonical = canonizedCandidate(response);
    // The model drops the imported id and adds the canonical (canonize action);
    // presentation just needs the new canonical's display data. The retired
    // imported row disappears because orderedCandidates no longer lists it.
    setPresentation((prev) => ({ ...prev, [canonical.id]: canonical }));
    dispatch({ type: 'canonize', importedId, canonical: toModel(canonical) });
    // Each Add as New is its own committed transaction; record it so the Undo
    // prompt can disclose it survives an undo (§3.14).
    onCanonized?.();
  };

  const handleSubmit = (
    overrideRationale?: string,
    falsePositiveIds?: number[]
  ) => {
    if (!preview) return;
    setError(null);
    const data: ReconcileCreateData = {
      entity_ids: Array.from(selectedIds).sort((a, b) => a - b),
      groups: buildGroupsPayload(state),
      version_tokens: preview.fingerprints,
      session_id: sessionId,
      ...(overrideRationale ? { override_rationale: overrideRationale } : {}),
      ...(falsePositiveIds?.length
        ? { false_positive_entity_ids: falsePositiveIds }
        : {}),
    };
    submitCreate(
      data,
      (result) => {
        setFlaggedForOverride(null);
        onComplete(result, namesById);
        onClose();
      },
      (err) => {
        if (isFlagOverrideRequired(err)) {
          setFlaggedForOverride(err.flaggedEntities);
          return;
        }
        // An unresolved-address-flag block: mark the named rows so the block is
        // visible, and name them in the error instead of the bare message that
        // points at nothing. Wording matches the proactive #26 banner so the same
        // rows aren't called two different things in the same failure.
        if (isReviewFlagRejection(err)) {
          markReviewFlagged(err.reviewFlaggedEntities.map((e) => e.id));
          // Name each record the app's standard way — "<name> (Entity ID: N)"
          // (entityRefLabel falls back to "Unknown entity" when the name is null).
          const labels = err.reviewFlaggedEntities.map((e) =>
            entityRefLabel(e.name, e.id)
          );
          setError(
            `${labels.join(', ')} ${
              labels.length === 1 ? 'has' : 'have'
            } an unresolved address-confidence flag (marked below). Resolve the flag or remove the entity from the reconciliation before reconciling.`
          );
          return;
        }
        if (err?.currentFingerprints) {
          handleStaleSelection(err.currentFingerprints);
          return;
        }
        setError(
          actionErrorMessage(err, 'Could not reconcile the selected entities')
        );
      }
    );
  };

  return (
    <>
      <FlagOverrideModal
        open={!!flaggedForOverride}
        flaggedEntities={flaggedForOverride ?? []}
        loading={createLoading}
        onCancel={() => setFlaggedForOverride(null)}
        onConfirm={(rationale, falsePositiveIds) =>
          handleSubmit(rationale, falsePositiveIds)
        }
        renderRowGraphLink={(id) => <RowAliasGraphLink entityId={id} />}
        floatingMenuSelectors={[ALIAS_CLUSTER_GRAPH_SELECTOR]}
      />
      <EntitySearchDialog
        isOpen={addEntitiesOpen}
        onClose={() => setAddEntitiesOpen(false)}
        selectedEntityIds={reconcileCandidates.map((c) => c.id)}
        primaryEntityId={primaryEntityId}
        primaryEntityType={primaryEntityType}
        allowedEntityTypes={addEntitiesAllowedTypes}
        onSelect={handleAddEntities}
      />
      {canonizeTarget && (
        <EntityCanonizePanel
          open
          entityId={canonizeTarget.id}
          name={canonizeTarget.name}
          sessionId={sessionId}
          establishedType={establishedCanonicalType(state)}
          subjectInReconciliation={selectedIds.has(canonizeTarget.id)}
          existingEntities={orderedCandidates(state)
            .filter((c) => c.id !== canonizeTarget.id)
            .map((c) => ({ id: c.id, name: c.name, type: c.type }))}
          onClose={() => setCanonizeTargetId(null)}
          onCanonized={handleCanonized}
        />
      )}
      <Drawer
        lockBackgroundScroll
        open={open}
        direction="bottom"
        size="90%"
        onClose={onClose}
        className={styles.tearsheet}
      >
        <DrawerHeader>
          <div className={styles.tearsheetHeader}>
            <h3>{title}</h3>
            <CarbonButton
              hasIconOnly
              size="lg"
              kind="ghost"
              onClick={onClose}
              iconDescription="Close"
              tooltipAlignment="center"
              tooltipPosition="bottom"
              renderIcon={(props) => <Close size={20} {...props} />}
            />
          </div>
        </DrawerHeader>
        <DrawerContent className={styles.drawerBody}>
          <div className={styles.body} ref={bodyRef}>
            <div className={styles.workArea} ref={workAreaRef}>
              <div className={styles.columns} ref={columnsRef}>
                <div className={styles.candidates}>
                  <div className={styles.paneHeader}>
                    <h4 className={styles.paneTitle}>
                      Current Suggestions
                      <Tooltip
                        align="bottom"
                        autoAlign
                        label="Library entities that may match this one, scored by the six-factor model. Select entities to deduplicate, or drag names in the right panel."
                      >
                        <button
                          type="button"
                          className={styles.paneInfoBtn}
                          aria-label="About Current Suggestions"
                        >
                          <Information size={16} />
                        </button>
                      </Tooltip>
                      {/* (checked of total) — the left number tracks entities with
                        their checkbox enabled. */}
                      <span className={styles.paneCount}>
                        ({selectedIds.size} of {reconcileCandidates.length})
                      </span>
                    </h4>
                  </div>
                  <div className={styles.paneBody}>
                    {leftNotifications.length > 0 && (
                      <Accordion className={styles.notifications}>
                        <AccordionItem
                          title={`Notifications (${leftNotifications.length})`}
                          open
                        >
                          {leftNotifications}
                        </AccordionItem>
                      </Accordion>
                    )}
                    {/* Toolbar + table as one flush block (Carbon table pattern). */}
                    <div className={styles.tableSection}>
                      <TableToolbar
                        size="sm"
                        className={styles.leftToolbar}
                        aria-label="Suggestions toolbar"
                      >
                        <TableToolbarContent>
                          <TableToolbarSearch
                            persistent
                            placeholder="Search suggestions"
                            value={candidateSearch}
                            onChange={(e) =>
                              setCandidateSearch(
                                (e as React.ChangeEvent<HTMLInputElement>)
                                  .target.value
                              )
                            }
                            onClear={() => setCandidateSearch('')}
                          />
                          <CarbonButton
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={candidateCompact ? Screen : Laptop}
                            iconDescription={
                              candidateCompact
                                ? 'Full-monitor mode'
                                : 'Laptop mode'
                            }
                            tooltipPosition="bottom"
                            onClick={() => setCandidateCompact((v) => !v)}
                          />
                          <CarbonButton
                            kind="ghost"
                            size="sm"
                            renderIcon={Add}
                            onClick={() => setAddEntitiesOpen(true)}
                          >
                            Add Entities
                          </CarbonButton>
                        </TableToolbarContent>
                      </TableToolbar>
                      <div
                        className={
                          candidateCompact ? styles.compactTable : undefined
                        }
                      >
                        <ReconcileCandidateTable
                          candidates={searchedCandidates}
                          selectedIds={selectedIds}
                          onToggle={onToggle}
                          onToggleAll={onToggleAll}
                          allSelected={allSelected}
                          someSelected={someSelected}
                          aliasStates={aliasStates}
                          operationTags={operationTags}
                          compact={candidateCompact}
                          highlightedIds={highlightedIds}
                          lockedIds={lockedIds}
                          addAsNewIds={addAsNewIds}
                          onAddAsNew={setCanonizeTargetId}
                          onFlagCleared={handleFlagCleared}
                          onViewDocument={handleViewDocument}
                          viewedDocumentId={viewedDocument?.id ?? null}
                          showOperatorTag={showOperatorTag}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={styles.dragger}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize consolidation panel"
                  onMouseDown={startResize}
                >
                  <span className={styles.draggerGrip} />
                </div>
                <div
                  className={styles.previewCol}
                  style={
                    previewWidth !== null
                      ? { flex: `0 0 ${previewWidth}px` }
                      : undefined
                  }
                >
                  {rightNotifications.length > 0 && (
                    <Accordion className={styles.notifications}>
                      <AccordionItem
                        title={`Notifications (${rightNotifications.length})`}
                        open
                      >
                        {rightNotifications}
                      </AccordionItem>
                    </Accordion>
                  )}
                  <EntityConsolidationPanel
                    state={state}
                    dispatch={dispatch}
                    selectedKeys={selectedNameKeys}
                    onSelectKeys={selectNames}
                    onFilterName={setFilterNameKey}
                    activeFilterKey={filterNameKey}
                    onConfirmRemoveName={handleConfirmRemoveName}
                  />
                  {/* Reconciliation Result Preview — per-survivor blocks (§3.3 /
                    mock v25): confirm the surviving record + primary address,
                    and expand the source documents behind each survivor. */}
                  <EntityConsolidationSurvivors
                    state={state}
                    dispatch={dispatch}
                    candidatesById={candidatesById}
                    highlightedEntityId={highlightedDocEntityId}
                    onHighlightEntity={highlightDocument}
                    onViewDocument={handleViewDocument}
                    viewedDocumentId={viewedDocument?.id ?? null}
                    showOperatorTag={showOperatorTag}
                  />
                </div>
              </div>
              {/* Doc viewer (side layout) sits OUTSIDE the resize container
                (.columns) so the pane-resize math only spans candidates +
                preview — dragging the divider broke the layout when the viewer
                was open (Erik feedback). */}
              {viewedDocument && docImageLayout === 'side' && (
                <>
                  <div
                    className={styles.dragger}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize document image"
                    onMouseDown={startDocResize}
                  >
                    <span className={styles.draggerGrip} />
                  </div>
                  <div
                    className={styles.docImageCol}
                    style={
                      docImageWidth !== null
                        ? { flex: `0 0 ${docImageWidth}px` }
                        : undefined
                    }
                  >
                    <DocImagePanel
                      documentId={viewedDocument.id}
                      documentName={viewedDocument.name}
                      onClose={() => setViewedDocument(null)}
                      initialLayout="side"
                      onLayoutChange={setDocImageLayout}
                    />
                  </div>
                </>
              )}
            </div>
            {viewedDocument && docImageLayout === 'stacked' && (
              <>
                <div
                  className={styles.draggerH}
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label="Resize document image"
                  onMouseDown={startDocVResize}
                >
                  <span className={styles.draggerGripH} />
                </div>
                <DocImagePanel
                  documentId={viewedDocument.id}
                  documentName={viewedDocument.name}
                  onClose={() => setViewedDocument(null)}
                  initialLayout="stacked"
                  onLayoutChange={setDocImageLayout}
                  style={
                    docImageHeight !== null
                      ? { flex: `0 0 ${docImageHeight}px` }
                      : undefined
                  }
                />
              </>
            )}
          </div>
        </DrawerContent>
        <div className={styles.footer}>
          <CarbonButton kind="secondary" onClick={onClose}>
            Cancel
          </CarbonButton>
          <CarbonButton
            kind="primary"
            disabled={!canSubmit}
            onClick={() => handleSubmit()}
          >
            Reconcile
          </CarbonButton>
        </div>
      </Drawer>
      {/* §3.3 removal confirmation. Fires for the right-panel trashcan, a
          left-panel deselect, or a select-all clear that would empty a survivor
          with deduped names. */}
      <SurvivorResetModal
        open={reset.pending !== null}
        onConfirm={reset.confirm}
        onCancel={reset.cancel}
      />
    </>
  );
}

export default ConsolidationTearsheet;
